import { DATE } from '@/constants/labels';
import { format } from 'date-fns';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { MdArrowDropDown, MdCheck, MdChevronLeft, MdChevronRight, MdToday } from 'react-icons/md';
import { Row } from '../container/Container';
import styles from './dropdown-with-date-picker.module.css';
import { FieldValues, Path } from 'react-hook-form';
import DropdownInput from '../inputs/DropdownInput';
import moment from 'moment';

type Option = {
	label: string;
	value: string | number;
};

type DateRange = {
	start: Date | null;
	end: Date | null;
};

type Props<T extends FieldValues> = {
	date: string | number;
	setDate: Dispatch<SetStateAction<string | number>>;
	options: Option[];
	dateRange: DateRange | null;
	setDateRange: React.Dispatch<React.SetStateAction<DateRange | null>>;
	id: Path<T>;
};
/* The abstraction of constrol value here is currently broken. Client should handle two values here instead of one as usually: date and dateRange. I have no time to refactor it currently.*/
const DropdownWithDatePicker = <T extends FieldValues>(props: Props<T>) => {
	const { date, setDate, options, dateRange, setDateRange } = props;

	const [isOpen, setIsOpen] = useState(false);
	const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
	const [yearDropdownOpen, setYearDropdownOpen] = useState(false);

	useEffect(() => {
		// we have onClick handler, but it doesn't work always immediately after page reload.
		if (date === 'custom') {
			setIsOpen(true);
		}
	}, [date]);

	const handleChange = (dates: Date | [Date | null, Date | null] | null) => {
		if (Array.isArray(dates)) {
			const [start, end] = dates;
			setDateRange({ start, end });
		}
	};

	// //TODO: FIX RENDER OF SELECTED CALENDAR RANGE
	// // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	// useEffect(() => {
	// 	// console.log(selectedDate2);
	// 	// const toConvert = selectedDate2?.toString();
	// 	// console.log(selectedDate2);
	// 	// setSelectedDate(selectedDate2?.getDate());
	// 	// format(selectedDate2?.toString(), 'MMMM dd, yyyy');
	// }, [selectedDate2]);



	const selectedOptionTemplate = (option: Option) => {
		return date !== 'custom' ||
			!dateRange ||
			!dateRange.start ||
			!dateRange.end ? (
			<Row>
				<MdToday size={24} className="mr-2 text-onSurfaceVariant" />
				{option?.label}
			</Row>
		) : (
			<Row>
				<MdToday size={24} className="mr-2 text-onSurfaceVariant" />
				{`${format(dateRange.start, 'MMM d')} -
				${format(dateRange.end, 'MMM d, yyyy')}`}
			</Row>
		);
	};

	return (
		<>
			<div className={styles.customDatepicker}>
				<DropdownInput
					variant="outlined"
					id={props.id}
					containerCls="shrink-0"
					value={date}
					options={options}
					onChange={(e) => {
						setDate(e)
					}
					}


					label={DATE}
					labelBgColor="bg-surface"
					itemTemplate={(option: Option) => {
						return (
							<div
								style={{
									justifyContent: 'space-between',
									display: 'flex',
									cursor: 'pointer',
								}}
								onClick={(e) => {
									if (option.value === 'custom') {
										setIsOpen(true);
									}
								}}
							>
								{option.label}
							</div>
						);
					}}
					valueTemplate={selectedOptionTemplate}
					panelClassName="!w-[280px] !left-[56px]"
					scrollable={false}
				/>

				<div className={styles.datepickerWrapper}>
					<DatePicker
						selected={dateRange?.start}
						onChange={handleChange}
						startDate={dateRange?.start}
						endDate={dateRange?.end}
						selectsRange
						shouldCloseOnSelect={false}
						dateFormat="MMMM d, yyyy"
						className={styles.customDatepickerInput}
						showMonthDropdown
						showYearDropdown
						popperClassName="react-datepicker-popper"
						open={isOpen}
						minDate={moment().year(2025).startOf('year').toDate()}
						onClickOutside={() => setIsOpen(false)}
						renderCustomHeader={({
							date,
							decreaseMonth,
							increaseMonth,
							prevYearButtonDisabled,
							nextYearButtonDisabled,
							decreaseYear,
							increaseYear,
							changeMonth,
							changeYear,
						}) => {
							const years = Array.from(
								{ length: 50 },
								(_, i) => 2025 + i,
							);
							const months = Array.from({ length: 12 }, (_, i) =>
								new Date(0, i).toLocaleString('default', {
									month: 'short',
								}),
							);

							return (
								<div className="flex flex-col">
									<div className={styles.monthYearDropdown}>
										<div className="flex items-center gap-3">

											<div
												className={
													styles.selectContainer
												}
											>
												<div
													className={styles.customSelect}
													onClick={() => setMonthDropdownOpen(!monthDropdownOpen)}
												>
													<span className={styles.selectValue}>
														{months[date.getMonth()]}
													</span>
													<MdArrowDropDown
														size={18}
														className={
															styles.selectIcon
														}
													/>
												</div>

												{monthDropdownOpen && (
													<>
														<div
															className={styles.dropdownOverlay}
															onClick={() => setMonthDropdownOpen(false)}
														/>
														<div className={`${styles.dropdownMenu} ${styles.dropdownMenuMonth}`}>
															<div className={styles.dropdownDivider} />
															<div className={styles.dropdownItemsContainer}>
																{months.map(
																	(month, index) => (
																		<div
																			key={month}
																			className={`${styles.dropdownItem} ${styles.dropdownItemMonth}`}
																			onClick={() => {
																				changeMonth(index);
																				setMonthDropdownOpen(false);
																			}}
																		>
																			{date.getMonth() === index && (
																				<MdCheck
																					size={20}
																					className={styles.checkIcon}
																				/>
																			)}
																			{month}
																		</div>
																	),
																)}
															</div>
														</div>
													</>
												)}
											</div>

										</div>

										<div className="flex items-center gap-3">

											<div
												className={
													styles.selectContainer
												}
											>
												<div
													className={styles.customSelect}
													onClick={() => setYearDropdownOpen(!yearDropdownOpen)}
												>
													<span className={styles.selectValue}>
														{date.getFullYear()}
													</span>
													<MdArrowDropDown
														size={18}
														className={
															styles.selectIcon
														}
													/>
												</div>
												{yearDropdownOpen && (
													<>
														<div
															className={styles.dropdownOverlay}
															onClick={() => setYearDropdownOpen(false)}
														/>
														<div className={`${styles.dropdownMenu} ${styles.dropdownMenuYear}`}>
															<div className={styles.dropdownDivider} />
															<div className={styles.dropdownItemsContainer}>
																{years.map((year) => (
																	<div
																		key={year}
																		className={`${styles.dropdownItem} ${styles.dropdownItemYear}`}
																		onClick={() => {
																			changeYear(year);
																			setYearDropdownOpen(false);
																		}}
																	>
																		{date.getFullYear() === year && (
																			<MdCheck
																				size={20}
																				className={styles.checkIcon}
																			/>
																		)}
																		{year}
																	</div>
																))}
															</div>
														</div>
													</>
												)}
											</div>

										</div>
									</div>
								</div>
							);
						}}
						calendarContainer={({ children }) => (
							<div>
								{children}{' '}
								<div className={styles.footer}>
									<button
										type="button"
										className={styles.footerButton}
										onClick={() => setDateRange(null)}
									>
										Clear
									</button>
									<div className="flex gap-2">
										<button
											type="button"
											className={styles.footerButton}
											onClick={() => setIsOpen(false)}
										>
											Cancel
										</button>
										<button
											type="button"
											className={styles.footerButton}
											onClick={() => setIsOpen(false)}
										>
											OK
										</button>
									</div>
								</div>
							</div>
						)}
					/>
				</div>
			</div>
		</>
	);
};

export default DropdownWithDatePicker;
